import { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import {
  buildError,
  Campaign,
  CampaignValueCards as CampaignValueCardsResponse,
  CampaignValueCardSet,
  useToast,
} from '@aslanpay/portal-common-lib';

import CenterSpinner from 'app/components/UI/CenterSpinner';
import useFetchCampaignValueCards from 'app/hooks/useFetchCampaignValueCards';
import useFetchRecognitionCards from 'app/hooks/useFetchRecognitionCards';
import useFetchRecognitionValues from 'app/hooks/useFetchRecognitionValues';
import useRecognitionCardMutations from 'app/hooks/useRecognitionCardMutations';
import useUpdateCampaignValueCards from 'app/hooks/useUpdateCampaignValueCards';
import { uploadRecognitionCardImage } from 'app/utils/recognitionCardUpload';

import ValueCardsEditor, { CardOption, ValueCardSelection } from './ValueCardsEditor';

interface CampaignValueCardsProps {
  campaign: Campaign;
  companyId: string;
  prideNames: Map<string, string>;
}

const toSelection = (sets: CampaignValueCardSet[]): ValueCardSelection =>
  Object.fromEntries(sets.map((set) => [set.valueId, set.cards.map((card) => card.id)]));

const savedCardsOf = (valueCards: CampaignValueCardsResponse | undefined): CardOption[] => [
  ...(valueCards?.defaults ?? []).flatMap((set) => set.cards),
  ...(valueCards?.groups ?? []).flatMap((group) => group.values.flatMap((set) => set.cards)),
];

/** Only values with a card ticked are sent; a value left with none has no cards of its own. */
const toRequestValues = (selection: ValueCardSelection, valueIds: string[]) =>
  valueIds
    .filter((valueId) => selection[valueId]?.length)
    .map((valueId) => ({ valueId, cardIds: selection[valueId] }));

/**
 * Which cards each value is announced with: a default for the campaign, and for a campaign that fans out, any pride's
 * own. Ticking a card here changes the campaign's choice only, never the value's own peer cards.
 */
function CampaignValueCards({ campaign, companyId, prideNames }: CampaignValueCardsProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [resettingGroupId, setResettingGroupId] = useState<string>();

  const valueCardsQuery = useFetchCampaignValueCards(campaign.id);
  const valuesQuery = useFetchRecognitionValues({ companyId, isEnabled: true }, !!companyId);
  const libraryCardsQuery = useFetchRecognitionCards({ companyId }, !!companyId);
  const campaignCardsQuery = useFetchRecognitionCards(
    { companyId, usage: 'CAMPAIGN' },
    !!companyId
  );
  const { createCard } = useRecognitionCardMutations();

  const prides = useMemo(
    () =>
      (campaign.children ?? [])
        .filter((child) => child.groupId)
        .map((child) => ({
          groupId: child.groupId as string,
          name: prideNames.get(child.groupId as string) ?? 'Unknown pride',
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [campaign.children, prideNames]
  );

  // Birthdays and work anniversaries are celebrations, not values a campaign is won under, so they get no cards.
  const values = useMemo(
    () => (valuesQuery.data?.list ?? []).filter((value) => value.type !== 'REASON'),
    [valuesQuery.data]
  );
  const valueIds = values.map((value) => value.id);
  const libraryCards = useMemo(
    () => [...(libraryCardsQuery.data?.list ?? []), ...(campaignCardsQuery.data?.list ?? [])],
    [libraryCardsQuery.data, campaignCardsQuery.data]
  );
  const valueCards = valueCardsQuery.data;
  const savedCards = useMemo(() => savedCardsOf(valueCards), [valueCards]);
  const defaultSelection = useMemo(() => toSelection(valueCards?.defaults ?? []), [valueCards]);

  const invalidateValueCards = () =>
    queryClient.invalidateQueries({ queryKey: ['fetchCampaignValueCards', campaign.id] });

  const { mutate: update, isLoading: isSaving } = useUpdateCampaignValueCards({
    onSuccess: (_saved, { request }) => {
      const isReset = !!request.groupId && !request.values.length;
      toast({
        position: 'top-right',
        status: 'success',
        title: isReset ? 'Back to the default' : 'Cards saved',
        description: isReset
          ? 'This pride now announces with the default cards.'
          : 'Winners will be announced with the cards you chose.',
      });
      invalidateValueCards();
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not save the cards',
        description: buildError(error, 'Nothing was changed. Please try again.'),
      }),
    onSettled: () => setResettingGroupId(undefined),
  });

  // A save replaces the whole scope, so cards already saved for values this page doesn't list (a disabled value, or a
  // celebration) are sent back unchanged rather than dropped.
  const unlistedSavedValues = (groupId?: string) => {
    const savedSets = groupId
      ? valueCards?.groups.find((group) => group.groupId === groupId)?.values ?? []
      : valueCards?.defaults ?? [];

    return savedSets
      .filter((set) => !valueIds.includes(set.valueId))
      .map((set) => ({ valueId: set.valueId, cardIds: set.cards.map((card) => card.id) }));
  };

  const save = (selection: ValueCardSelection, groupId?: string) =>
    update({
      campaignId: campaign.id,
      request: {
        groupId,
        values: [...toRequestValues(selection, valueIds), ...unlistedSavedValues(groupId)],
      },
    });

  const reset = (groupId: string) => {
    setResettingGroupId(groupId);
    update({ campaignId: campaign.id, request: { groupId, values: [] } });
  };

  const upload = async (file: File): Promise<string | null> => {
    try {
      const contentUrl = await uploadRecognitionCardImage(file, companyId);
      const card = await createCard.mutateAsync({ companyId, contentUrl, usage: 'CAMPAIGN' });
      await queryClient.invalidateQueries({
        queryKey: ['fetchRecognitionCards', companyId, 'CAMPAIGN'],
      });

      return card.id;
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not upload card',
        description: 'The image could not be uploaded. Please try again.',
      });

      return null;
    }
  };

  // The editors pick each value's starting card once, on mount, so the card library must be in by then.
  if (
    valueCardsQuery.isInitialLoading ||
    valuesQuery.isInitialLoading ||
    libraryCardsQuery.isInitialLoading ||
    campaignCardsQuery.isInitialLoading
  ) {
    return <CenterSpinner />;
  }

  if (valueCardsQuery.isError || valuesQuery.isError) {
    return (
      <Flex flexDir="column" align="flex-start" gap="12px" data-testid="value-cards-error">
        <Text textStyle="smallRegular" color="text.error">
          The campaign&apos;s cards could not be loaded.
        </Text>
        <Button
          variant="outline"
          onClick={() => {
            valueCardsQuery.refetch();
            valuesQuery.refetch();
          }}
          data-testid="retry-value-cards"
        >
          Try again
        </Button>
      </Flex>
    );
  }

  if (!values.length) {
    return (
      <Text textStyle="smallRegular" color="brand.darkGray" data-testid="value-cards-empty">
        This company has no enabled values yet. Add them under Settings, Reward &amp; Recognition.
      </Text>
    );
  }

  // Remounted only when the saved choice changes, so a tab starts from what is saved. Keyed on the content rather
  // than dataUpdatedAt: a background refetch (e.g. on window focus) returns the same choice and must not wipe
  // ticks the admin has not saved yet.
  const version = JSON.stringify(valueCardsQuery.data);

  const editorProps = {
    values,
    libraryCards,
    savedCards,
    // A reset shows its own spinner, but Save stays disabled while any update is in flight.
    isSaving: isSaving && !resettingGroupId,
    isUpdating: isSaving,
    onUpload: upload,
  };

  if (!prides.length) {
    return (
      <ValueCardsEditor
        key={version}
        {...editorProps}
        initialSelection={defaultSelection}
        description="Choose at least one card for each value. This campaign's winners are announced with them."
        onSave={(selection) => save(selection)}
      />
    );
  }

  return (
    <Tabs variant="unstyled" isLazy data-testid="value-cards-tabs">
      <TabList gap="8px" borderBottom="1px solid" borderColor="brand.lightGray1" flexWrap="wrap">
        <Tab
          px="16px"
          py="10px"
          mb="-1px"
          whiteSpace="nowrap"
          color="brand.darkGray"
          fontWeight="medium"
          borderBottom="2px solid"
          borderColor="transparent"
          _hover={{ color: 'brand.purple' }}
          _selected={{ color: 'brand.purple', borderColor: 'brand.purple' }}
          data-testid="value-cards-tab-default"
        >
          Default
        </Tab>
        {prides.map((pride) => (
          <Tab
            key={pride.groupId}
            px="16px"
            py="10px"
            mb="-1px"
            whiteSpace="nowrap"
            maxW="240px"
            color="brand.darkGray"
            fontWeight="medium"
            borderBottom="2px solid"
            borderColor="transparent"
            _hover={{ color: 'brand.purple' }}
            _selected={{ color: 'brand.purple', borderColor: 'brand.purple' }}
            title={pride.name}
            data-testid={`value-cards-tab-${pride.groupId}`}
          >
            <Text as="span" noOfLines={1}>
              {pride.name}
            </Text>
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        <TabPanel px="0" pt="24px" pb="0">
          <ValueCardsEditor
            key={`default-${version}`}
            {...editorProps}
            initialSelection={defaultSelection}
            description="Choose at least one card for each value. Prides use these unless they have their own."
            onSave={(selection) => save(selection)}
          />
        </TabPanel>
        {prides.map((pride) => {
          const saved = valueCards?.groups.find((group) => group.groupId === pride.groupId);

          return (
            <TabPanel key={pride.groupId} px="0" pt="24px" pb="0">
              <ValueCardsEditor
                key={`${pride.groupId}-${version}`}
                {...editorProps}
                groupId={pride.groupId}
                initialSelection={saved ? toSelection(saved.values) : defaultSelection}
                description="Choose at least one card for each value. Saving here overrides the default for this pride."
                usingDefault={!saved}
                isResetting={resettingGroupId === pride.groupId}
                onSave={(selection) => save(selection, pride.groupId)}
                onReset={() => reset(pride.groupId)}
              />
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  );
}

export default CampaignValueCards;
